import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import CourseCard from "@/components/courses/CourseCard";
import SectionBackground from "@/components/layout/SectionBackground";
import CourseBundleSelector from "@/components/courses/CourseBundleSelector";
import { useCourses } from "@/hooks/useCourses";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";
import { useStripePayment } from "@/hooks/useStripePayment";
import { useStripePricing } from "@/hooks/useStripePricing";
import { useSyncStripePricing } from "@/hooks/useSyncStripePricing";
import { supabase } from "@/services/supabase";

interface SelectedCourse {
  id: string;
  name: string;
  price: number; // Price in cents
  priceId: string;
  isAvailable: boolean;
}

const Pricing = () => {
  const navigate = useNavigate();
  
  // Fetch courses with real-time updates
  const { courses, loading, error } = useCourses({
    enableRealtime: true,
    autoRefresh: true,
    refreshInterval: 300000, // Refresh every 5 minutes
  });

  const { createCheckout, loading: checkoutLoading } = useStripeCheckout();
  const { createPayment, loading: paymentLoading } = useStripePayment();
  const [selectedBundleCourses, setSelectedBundleCourses] = useState<SelectedCourse[]>([]);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  
  // Fetch dynamic pricing from Supabase (SINGLE SOURCE OF TRUTH)
  const { 
    data: pricingData, 
    loading: pricingLoading, 
    isReady: pricingReady,
    getPrice,
    getPriceId,
    formatAmount,
  } = useStripePricing();
  const { syncPricing, loading: syncLoading } = useSyncStripePricing();

  // Get premium pricing
  const premiumPrice = useMemo(() => getPrice('premium', billingPeriod), [getPrice, billingPeriod]);
  const premiumPriceId = useMemo(() => getPriceId('premium', billingPeriod), [getPriceId, billingPeriod]);
  const premiumMonthlyPrice = useMemo(() => getPrice('premium', 'monthly'), [getPrice]);

  // Get course pricing
  const partnerworkPrice = useMemo(() => getPrice('partnerwork', billingPeriod), [getPrice, billingPeriod]);
  const leaderStylingPrice = useMemo(() => getPrice('leaderStyling', billingPeriod), [getPrice, billingPeriod]);
  const followerStylingPrice = useMemo(() => getPrice('followerStyling', billingPeriod), [getPrice, billingPeriod]);

  // Get Niveles pricing
  const nivelesPrice = useMemo(() => getPrice('niveles', 'one-time'), [getPrice]);
  const nivelesPriceId = useMemo(() => getPriceId('niveles', 'one-time'), [getPriceId]);

  // Bundle-eligible courses with dynamic pricing from Stripe
  const bundleEligibleCourses: SelectedCourse[] = useMemo(() => [
    {
      id: "partnerwork",
      name: "Partnerwork Course",
      price: partnerworkPrice?.amount || 0,
      priceId: getPriceId('partnerwork', billingPeriod) || '',
      isAvailable: Boolean(partnerworkPrice?.amount),
    },
    {
      id: "leaderStyling",
      name: "Leader Styling Course",
      price: leaderStylingPrice?.amount || 0,
      priceId: getPriceId('leaderStyling', billingPeriod) || '',
      isAvailable: Boolean(leaderStylingPrice?.amount),
    },
    {
      id: "followerStyling",
      name: "Follower Styling Course",
      price: followerStylingPrice?.amount || 0,
      priceId: getPriceId('followerStyling', billingPeriod) || '',
      isAvailable: Boolean(followerStylingPrice?.amount),
    },
  ], [partnerworkPrice, leaderStylingPrice, followerStylingPrice, getPriceId, billingPeriod]);

  const handlePremiumClick = async () => {
    if (!premiumPriceId) {
      console.error('[Pricing] Premium price not available');
      return;
    }
    await createCheckout(premiumPriceId);
  };

  const handleNivelesPayment = async () => {
    if (!nivelesPriceId) {
      console.error('[Pricing] Niveles price not available');
      return;
    }
    await createPayment(nivelesPriceId);
  };

  const handleCourseClick = async (courseTitle: string) => {
    // Check for Niveles choreography - one-time payment
    if (courseTitle.toUpperCase().includes('NIVELES') || courseTitle.toUpperCase().includes('CHOREOGRAPHY')) {
      return handleNivelesPayment();
    }
    
    let priceId: string | null = null;
    
    if (courseTitle.includes('Partnerwork')) {
      priceId = getPriceId('partnerwork', billingPeriod);
    } else if (courseTitle.includes('Leader Styling')) {
      priceId = getPriceId('leaderStyling', billingPeriod);
    } else if (courseTitle.includes('Follower Styling')) {
      priceId = getPriceId('followerStyling', billingPeriod);
    }
    
    if (!priceId) {
      console.error('[Pricing] Course price not available for:', courseTitle);
      return;
    }
    
    await createCheckout(priceId);
  };

  const [bundleCheckoutLoading, setBundleCheckoutLoading] = useState(false);

  const handleBundleCheckout = async () => {
    if (selectedBundleCourses.length !== 2) {
      return;
    }

    // Verify all selected courses have valid price IDs
    const invalidCourses = selectedBundleCourses.filter(c => !c.priceId);
    if (invalidCourses.length > 0) {
      console.error('[Pricing] Some bundle courses have invalid prices');
      return;
    }

    const priceIds = selectedBundleCourses.map(course => course.priceId);
    await createBundleCheckout(priceIds);
  };

  const createBundleCheckout = async (priceIds: string[]) => {
    try {
      setBundleCheckoutLoading(true);
      const { data, error } = await supabase.functions.invoke('create-bundle-checkout', {
        body: {
          price_ids: priceIds,
          discount_percent: 20,
        },
      });

      if (error || !data?.url) {
        throw new Error(error?.message || 'Failed to create bundle checkout');
      }

      window.location.href = data.url;
    } catch (error) {
      alert('Bundle checkout failed. Please try purchasing courses individually.');
    } finally {
      setBundleCheckoutLoading(false);
    }
  };

  // Helper to format price for display
  const displayPrice = (price: ReturnType<typeof getPrice>): string => {
    if (!price) return "Unavailable";
    return formatAmount(price.amount, price.currency);
  };

  // Calculate total individual course price for comparison
  const totalIndividualPrice = useMemo(() => {
    const prices = [partnerworkPrice, leaderStylingPrice, followerStylingPrice];
    const total = prices.reduce((sum, p) => sum + (p?.amount || 0), 0);
    return total;
  }, [partnerworkPrice, leaderStylingPrice, followerStylingPrice]);

  // Check if pricing is available
  const isPricingAvailable = pricingReady && premiumPrice && partnerworkPrice;

  return (
    <main 
      className="relative min-h-screen pt-20 lg:pt-[88px]"
      style={{
        background: "linear-gradient(180deg, #000000 0%, #0a0a0f 50%, #000000 100%)",
      }}
    >
      {/* Hero Section */}
      <section className="relative pt-20 pb-8 px-4 overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            style={{
              filter: "brightness(0.4)",
            }}
          >
            <source src="https://media.klauyros.artoverse.club/pricingbackground.webm" type="video/webm" />
          </video>
          {/* Dark overlay for better text readability */}
          <div 
            className="absolute inset-0"
            style={{
              background: "linear-gradient(180deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.8) 100%)",
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-4"
          >
            <span 
              className="text-sm uppercase tracking-wider"
              style={{
                color: "#E67E22",
                fontFamily: "'Grift', sans-serif",
                letterSpacing: "0.15em",
              }}
            >
              Elevate Your Dance Journey
            </span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6"
            style={{
              fontFamily: "serif",
              color: "#FFFFFF",
              textShadow: "0 0 60px rgba(230, 126, 34, 0.3)",
              letterSpacing: "-0.02em",
            }}
          >
            Choose Your Path
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg md:text-xl max-w-2xl mx-auto mb-12"
            style={{ 
              fontFamily: "'Grift', sans-serif",
              color: "rgba(255, 255, 255, 0.6)",
            }}
          >
            Unlock premium dance courses and elevate your skills with our expert-led programs
          </motion.p>

          {/* Note: All prices in EUR - Stripe will convert to your local currency in checkout */}
          <p 
            className="text-sm mb-4 text-center"
            style={{ 
              color: "rgba(255, 255, 255, 0.6)",
              fontFamily: "'Grift', sans-serif",
            }}
          >
            All prices in EUR • Stripe converts automatically to your currency at checkout
          </p>

          {/* Billing Period Toggle */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <span 
              className={`text-sm font-medium transition-colors ${
                billingPeriod === 'monthly' ? 'text-white' : 'text-white/50'
              }`}
              style={{ fontFamily: "'Grift', sans-serif" }}
            >
              Monthly
            </span>
            <button
              onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'yearly' : 'monthly')}
              className="relative w-14 h-7 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-transparent"
              style={{
                background: billingPeriod === 'yearly' 
                  ? 'linear-gradient(135deg, #E67E22 0%, #FF9E4F 100%)' 
                  : 'rgba(255, 255, 255, 0.2)',
              }}
            >
              <span
                className="absolute top-1 left-1 w-5 h-5 rounded-full bg-white shadow-lg transition-transform"
                style={{
                  transform: billingPeriod === 'yearly' ? 'translateX(28px)' : 'translateX(0)',
                }}
              />
            </button>
            <div className="flex items-center gap-2">
              <span 
                className={`text-sm font-medium transition-colors ${
                  billingPeriod === 'yearly' ? 'text-white' : 'text-white/50'
                }`}
                style={{ fontFamily: "'Grift', sans-serif" }}
              >
                Yearly
              </span>
              {billingPeriod === 'yearly' && (
                <span 
                  className="px-2 py-0.5 rounded-full text-xs font-semibold"
                  style={{
                    background: "#22C55E",
                    color: "#000000",
                    fontFamily: "'Grift', sans-serif",
                  }}
                >
                  Save 20%
                </span>
              )}
            </div>
          </div>

          {/* Loading State for Pricing */}
          {pricingLoading && (
            <div className="text-center py-8">
              <Loader2 className="animate-spin mx-auto mb-2" size={24} color="#E67E22" />
              <p style={{ color: "rgba(255, 255, 255, 0.6)", fontFamily: "'Grift', sans-serif" }}>
                Loading prices...
              </p>
            </div>
          )}

          {/* Pricing Cards in Hero */}
          {!pricingLoading && (
          <div className="max-w-5xl mx-auto mt-8">
            <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* Individual Courses Card */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="rounded-3xl p-8 transition-all duration-300"
              style={{
                background: "linear-gradient(135deg, rgba(255, 255, 255, 0.07) 0%, rgba(255, 255, 255, 0.03) 100%)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(255, 255, 255, 0.15)",
                boxShadow: "0 0 40px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.02)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              <h3 
                className="text-2xl font-semibold mb-2"
                style={{
                  fontFamily: "serif",
                  color: "#FFFFFF",
                }}
              >
                Individual Courses
              </h3>
              <div className="mb-6">
                <div className="flex items-baseline gap-2 mb-2">
                  {totalIndividualPrice > 0 && (
                    <span 
                      className="line-through text-lg"
                      style={{ color: "rgba(255, 255, 255, 0.4)" }}
                    >
                      {formatAmount(totalIndividualPrice, 'EUR')}
                    </span>
                  )}
                </div>
                <p 
                  className="text-sm"
                  style={{ 
                    color: "rgba(255, 255, 255, 0.6)",
                    fontFamily: "'Grift', sans-serif",
                  }}
                >
                  Choose courses individually
                </p>
              </div>

              {/* Course List */}
              <div className="space-y-4 mb-8">
                <div 
                  className="flex justify-between items-center py-3"
                  style={{ borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}
                >
                  <span 
                    style={{ 
                      color: "#FFFFFF",
                      fontFamily: "'Grift', sans-serif",
                    }}
                  >
                    Partnerwork Course
                  </span>
                  <div className="flex flex-col items-end">
                    <span 
                      className="font-medium"
                      style={{ 
                        color: partnerworkPrice ? "#FFFFFF" : "rgba(255, 255, 255, 0.4)",
                        fontFamily: "'Grift', sans-serif",
                      }}
                    >
                      {displayPrice(partnerworkPrice)}
                      {billingPeriod === 'yearly' && partnerworkPrice && '/year'}
                    </span>
                  </div>
                </div>
                <div 
                  className="flex justify-between items-center py-3"
                  style={{ borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}
                >
                  <span 
                    style={{ 
                      color: "#FFFFFF",
                      fontFamily: "'Grift', sans-serif",
                    }}
                  >
                    Follower Styling Course
                  </span>
                  <div className="flex flex-col items-end">
                    <span 
                      className="font-medium"
                      style={{ 
                        color: followerStylingPrice ? "#FFFFFF" : "rgba(255, 255, 255, 0.4)",
                        fontFamily: "'Grift', sans-serif",
                      }}
                    >
                      {displayPrice(followerStylingPrice)}
                      {billingPeriod === 'yearly' && followerStylingPrice && '/year'}
                    </span>
                  </div>
                </div>
                <div 
                  className="flex justify-between items-center py-3"
                  style={{ borderBottom: "1px solid rgba(255, 255, 255, 0.1)" }}
                >
                  <span 
                    style={{ 
                      color: "#FFFFFF",
                      fontFamily: "'Grift', sans-serif",
                    }}
                  >
                    Leader Styling Course
                  </span>
                  <div className="flex flex-col items-end">
                    <span 
                      className="font-medium"
                      style={{ 
                        color: leaderStylingPrice ? "#FFFFFF" : "rgba(255, 255, 255, 0.4)",
                        fontFamily: "'Grift', sans-serif",
                      }}
                    >
                      {displayPrice(leaderStylingPrice)}
                      {billingPeriod === 'yearly' && leaderStylingPrice && '/year'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <div
                    className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
                    style={{ background: "#22C55E" }}
                  >
                    <Check size={14} color="white" strokeWidth={3} />
                  </div>
                  <span 
                    style={{ 
                      color: "rgba(255, 255, 255, 0.8)",
                      fontFamily: "'Grift', sans-serif",
                    }}
                  >
                    24/7 access to purchased courses
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <div
                    className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
                    style={{ background: "#22C55E" }}
                  >
                    <Check size={14} color="white" strokeWidth={3} />
                  </div>
                  <span 
                    style={{ 
                      color: "rgba(255, 255, 255, 0.8)",
                      fontFamily: "'Grift', sans-serif",
                    }}
                  >
                    High-quality video content
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <div
                    className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
                    style={{ background: "#22C55E" }}
                  >
                    <Check size={14} color="white" strokeWidth={3} />
                  </div>
                  <span 
                    style={{ 
                      color: "rgba(255, 255, 255, 0.8)",
                      fontFamily: "'Grift', sans-serif",
                    }}
                  >
                    Certificate of completion
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <div
                    className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
                    style={{ background: "#22C55E" }}
                  >
                    <Check size={14} color="white" strokeWidth={3} />
                  </div>
                  <span 
                    style={{ 
                      color: "rgba(255, 255, 255, 0.8)",
                      fontFamily: "'Grift', sans-serif",
                    }}
                  >
                    Community forum access
                  </span>
                </li>
              </ul>

              <button
                onClick={() => {
                  const coursesSection = document.querySelector('#courses-section');
                  coursesSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className="w-full py-4 px-6 rounded-xl font-medium transition-all duration-300 cursor-pointer"
                style={{
                  background: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  color: "#FFFFFF",
                  fontFamily: "'Grift', sans-serif",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)";
                }}
              >
                Choose Individual Courses
              </button>
            </motion.div>

            {/* Premium Access Card - Highlighted */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="rounded-3xl p-8 transition-all duration-300 relative"
              style={{
                background: "linear-gradient(135deg, rgba(230, 126, 34, 0.15) 0%, rgba(255, 158, 79, 0.08) 100%)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(230, 126, 34, 0.3)",
                boxShadow: "0 0 60px rgba(230, 126, 34, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.02)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              <div
                className="absolute -top-4 left-1/2 px-4 py-1 rounded-full text-sm font-semibold"
                style={{
                  transform: "translateX(-50%)",
                  background: "#E67E22",
                  color: "#000000",
                  fontFamily: "'Grift', sans-serif",
                  boxShadow: "0 4px 20px rgba(230, 126, 34, 0.5)",
                }}
              >
                BEST VALUE
              </div>
              
              <h3 
                className="text-2xl font-semibold mb-2"
                style={{
                  fontFamily: "serif",
                  color: "#FFFFFF",
                }}
              >
                Premium Access
              </h3>
              <div className="mb-6">
                <div className="flex items-baseline gap-2">
                  {billingPeriod === 'yearly' && premiumMonthlyPrice && (
                    <span 
                      className="line-through text-2xl mr-2"
                      style={{ 
                        color: "rgba(255, 255, 255, 0.4)",
                        fontFamily: "serif",
                      }}
                    >
                      {formatAmount(premiumMonthlyPrice.amount * 12, 'EUR')}
                    </span>
                  )}
                  <span 
                    className="text-5xl font-bold"
                    style={{
                      fontFamily: "serif",
                      color: premiumPrice ? "#E67E22" : "rgba(255, 255, 255, 0.4)",
                      textShadow: premiumPrice ? "0 0 30px rgba(230, 126, 34, 0.4)" : "none",
                    }}
                  >
                    {displayPrice(premiumPrice)}
                  </span>
                  {premiumPrice && (
                    <span 
                      style={{ 
                        color: "rgba(255, 255, 255, 0.6)",
                        fontFamily: "'Grift', sans-serif",
                      }}
                    >
                      {billingPeriod === 'monthly' ? '/month' : '/year'}
                    </span>
                  )}
                </div>
                <p 
                  className="text-sm mt-2"
                  style={{ 
                    color: "rgba(255, 255, 255, 0.8)",
                    fontFamily: "'Grift', sans-serif",
                  }}
                >
                  All courses + future content
                </p>
                {billingPeriod === 'yearly' && premiumPrice && (
                  <p 
                    className="text-xs mt-1"
                    style={{ 
                      color: "#22C55E",
                      fontFamily: "'Grift', sans-serif",
                    }}
                  >
                    Save with yearly billing
                  </p>
                )}
              </div>

              {/* Included Courses */}
              <div className="mb-8">
                <p 
                  className="text-sm font-semibold mb-3"
                  style={{
                    color: "#E67E22",
                    fontFamily: "'Grift', sans-serif",
                  }}
                >
                  ALL 3 COURSES INCLUDED:
                </p>
                <ul className="space-y-2">
                  <li 
                    className="flex items-center gap-2"
                    style={{ 
                      color: "rgba(255, 255, 255, 0.8)",
                      fontFamily: "'Grift', sans-serif",
                    }}
                  >
                    <div 
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: "#E67E22" }}
                    ></div>
                    Partnerwork Course
                  </li>
                  <li 
                    className="flex items-center gap-2"
                    style={{ 
                      color: "rgba(255, 255, 255, 0.8)",
                      fontFamily: "'Grift', sans-serif",
                    }}
                  >
                    <div 
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: "#E67E22" }}
                    ></div>
                    Follower Styling Course
                  </li>
                  <li 
                    className="flex items-center gap-2"
                    style={{ 
                      color: "rgba(255, 255, 255, 0.8)",
                      fontFamily: "'Grift', sans-serif",
                    }}
                  >
                    <div 
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: "#E67E22" }}
                    ></div>
                    Leader Styling Course
                  </li>
                </ul>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <div
                    className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
                    style={{ background: "#22C55E" }}
                  >
                    <Check size={14} color="white" strokeWidth={3} />
                  </div>
                  <span 
                    style={{ 
                      color: "rgba(255, 255, 255, 0.8)",
                      fontFamily: "'Grift', sans-serif",
                    }}
                  >
                    Access to all current courses
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <div
                    className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
                    style={{ background: "#22C55E" }}
                  >
                    <Check size={14} color="white" strokeWidth={3} />
                  </div>
                  <span 
                    style={{ 
                      color: "rgba(255, 255, 255, 0.8)",
                      fontFamily: "'Grift', sans-serif",
                    }}
                  >
                    All future courses included
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <div
                    className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
                    style={{ background: "#22C55E" }}
                  >
                    <Check size={14} color="white" strokeWidth={3} />
                  </div>
                  <span 
                    style={{ 
                      color: "rgba(255, 255, 255, 0.8)",
                      fontFamily: "'Grift', sans-serif",
                    }}
                  >
                    Priority customer support
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <div
                    className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
                    style={{ background: "#22C55E" }}
                  >
                    <Check size={14} color="white" strokeWidth={3} />
                  </div>
                  <span 
                    style={{ 
                      color: "rgba(255, 255, 255, 0.8)",
                      fontFamily: "'Grift', sans-serif",
                    }}
                  >
                    Exclusive community events
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <div
                    className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
                    style={{ background: "#22C55E" }}
                  >
                    <Check size={14} color="white" strokeWidth={3} />
                  </div>
                  <span 
                    style={{ 
                      color: "rgba(255, 255, 255, 0.8)",
                      fontFamily: "'Grift', sans-serif",
                    }}
                  >
                    Cancel anytime
                  </span>
                </li>
              </ul>

              <button
                onClick={handlePremiumClick}
                disabled={checkoutLoading || !premiumPriceId}
                className="w-full py-4 px-6 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2"
                style={{
                  background: checkoutLoading || !premiumPriceId
                    ? "rgba(230, 126, 34, 0.5)" 
                    : "linear-gradient(135deg, #E67E22 0%, #FF9E4F 100%)",
                  color: "#000000",
                  fontFamily: "'Grift', sans-serif",
                  boxShadow: premiumPriceId ? "0 4px 30px rgba(230, 126, 34, 0.4)" : "none",
                  cursor: checkoutLoading || !premiumPriceId ? "not-allowed" : "pointer",
                }}
                onMouseEnter={(e) => {
                  if (!checkoutLoading && premiumPriceId) {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 6px 40px rgba(230, 126, 34, 0.6)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = premiumPriceId ? "0 4px 30px rgba(230, 126, 34, 0.4)" : "none";
                }}
              >
                {checkoutLoading ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    Processing...
                  </>
                ) : !premiumPriceId ? (
                  "Currently unavailable"
                ) : (
                  "Get Premium Access"
                )}
              </button>
            </motion.div>
            </div>

            {/* Course Bundle Card - 2 Courses 20% Off */}
            <div className="max-w-5xl mx-auto mt-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="rounded-3xl p-8 transition-all duration-300 relative"
                style={{
                  background: "linear-gradient(135deg, rgba(255, 255, 255, 0.07) 0%, rgba(255, 255, 255, 0.03) 100%)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255, 255, 255, 0.15)",
                  boxShadow: "0 0 40px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.02)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                <div
                  className="absolute -top-4 left-1/2 px-4 py-1 rounded-full text-sm font-semibold"
                  style={{
                    transform: "translateX(-50%)",
                    background: "#22C55E",
                    color: "#000000",
                    fontFamily: "'Grift', sans-serif",
                    boxShadow: "0 4px 20px rgba(34, 197, 94, 0.5)",
                  }}
                >
                  SAVE 20% ON 2 COURSES
                </div>

                <h3 
                  className="text-2xl font-semibold mb-2"
                  style={{
                    fontFamily: "serif",
                    color: "#FFFFFF",
                  }}
                >
                  Course Bundle
                </h3>

                <p 
                  className="text-sm mb-6"
                  style={{ 
                    color: "rgba(255, 255, 255, 0.6)",
                    fontFamily: "'Grift', sans-serif",
                  }}
                >
                  Select any 2 courses and save 20%
                </p>

                <CourseBundleSelector
                  courses={bundleEligibleCourses}
                  onBundleSelect={setSelectedBundleCourses}
                  formatPrice={(amountInCents) => formatAmount(amountInCents, 'EUR')}
                />

                <button
                  onClick={handleBundleCheckout}
                  disabled={selectedBundleCourses.length !== 2 || bundleCheckoutLoading || !selectedBundleCourses.every(c => c.isAvailable)}
                  className="w-full py-4 px-6 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 mt-6"
                  style={{
                    background: selectedBundleCourses.length === 2 && !bundleCheckoutLoading && selectedBundleCourses.every(c => c.isAvailable)
                      ? "linear-gradient(135deg, #E67E22 0%, #FF9E4F 100%)"
                      : bundleCheckoutLoading
                      ? "rgba(230, 126, 34, 0.5)"
                      : "rgba(255, 255, 255, 0.05)",
                    border: selectedBundleCourses.length === 2 && !bundleCheckoutLoading && selectedBundleCourses.every(c => c.isAvailable)
                      ? "none"
                      : "1px solid rgba(255, 255, 255, 0.2)",
                    color: selectedBundleCourses.length === 2 && selectedBundleCourses.every(c => c.isAvailable) ? "#000000" : "rgba(255, 255, 255, 0.5)",
                    fontFamily: "'Grift', sans-serif",
                    boxShadow: selectedBundleCourses.length === 2 && !bundleCheckoutLoading && selectedBundleCourses.every(c => c.isAvailable)
                      ? "0 4px 30px rgba(230, 126, 34, 0.4)" 
                      : "none",
                    cursor: selectedBundleCourses.length === 2 && !bundleCheckoutLoading && selectedBundleCourses.every(c => c.isAvailable) ? "pointer" : "not-allowed",
                  }}
                  onMouseEnter={(e) => {
                    if (selectedBundleCourses.length === 2 && !bundleCheckoutLoading && selectedBundleCourses.every(c => c.isAvailable)) {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow = "0 6px 40px rgba(230, 126, 34, 0.6)";
                    } else if (selectedBundleCourses.length !== 2) {
                      e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    if (selectedBundleCourses.length === 2 && !bundleCheckoutLoading && selectedBundleCourses.every(c => c.isAvailable)) {
                      e.currentTarget.style.boxShadow = "0 4px 30px rgba(230, 126, 34, 0.4)";
                    } else if (selectedBundleCourses.length !== 2) {
                      e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)";
                    }
                  }}
                >
                  {bundleCheckoutLoading ? (
                    <>
                      <Loader2 className="animate-spin" size={20} />
                      Processing...
                    </>
                  ) : selectedBundleCourses.length === 2 && selectedBundleCourses.every(c => c.isAvailable) ? (
                    `Purchase Bundle - ${formatAmount(
                      Math.round(selectedBundleCourses.reduce((sum, c) => sum + c.price, 0) * 0.8),
                      'EUR'
                    )}`
                  ) : (
                    "Select 2 Courses"
                  )}
                </button>
              </motion.div>
            </div>
          </div>
          )}

          {/* Scroll to Courses Link */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="text-center mt-12"
          >
            <button
              onClick={() => {
                const coursesSection = document.querySelector('#courses-section');
                coursesSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className="inline-flex items-center gap-2 transition-colors duration-300 bg-transparent border-none cursor-pointer"
              style={{
                color: "rgba(255, 255, 255, 0.6)",
                fontFamily: "'Grift', sans-serif",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "#E67E22";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "rgba(255, 255, 255, 0.6)";
              }}
            >
              Scroll to Explore All Courses ↓
            </button>
          </motion.div>
        </div>
      </section>

      {/* Courses Section */}
      <section id="courses-section" className="relative pt-4 pb-12 md:pb-16 px-4">
        <SectionBackground />
        
        <div className="relative max-w-7xl mx-auto">
          {/* Loading State */}
          {loading && courses.length === 0 && (
            <div className="text-center py-20">
              <div className="animate-pulse">
                <div className="h-8 bg-white/10 rounded-lg w-64 mx-auto mb-4"></div>
                <div className="h-4 bg-white/5 rounded-lg w-96 mx-auto"></div>
              </div>
            </div>
          )}
          
          {/* Error State */}
          {error && courses.length === 0 && (
            <div className="text-center py-20">
              <p className="text-red-400">Failed to load courses. Please try again later.</p>
            </div>
          )}
          
          {/* Course Cards */}
          {courses.length > 0 && (
          <>
            {/* First Row - Top 3 Courses */}
            <div className="relative grid md:grid-cols-2 lg:grid-cols-3 gap-8 px-4 md:px-0 mb-20">
            {courses.slice(0, 3).map((course, index) => {
              // Responsive ordering
              const orderClass = index === 0 ? "order-3 md:order-1" : 
                               index === 1 ? "order-1 md:order-2" : 
                               "order-2 md:order-3";

              const isNiveles = course.title.toUpperCase().includes('NIVELES') || course.title.toUpperCase().includes('CHOREOGRAPHY');
              const coursePrice = isNiveles ? nivelesPrice : partnerworkPrice;
              const ctaText = course.badge === "Invite Only" 
                ? "Apply Now" 
                : isNiveles
                  ? (nivelesPrice ? `Purchase - ${displayPrice(nivelesPrice)}` : "Currently unavailable")
                  : (coursePrice ? `Purchase - ${displayPrice(coursePrice)}` : "Currently unavailable");

              const isDisabled = course.badge !== "Invite Only" && !coursePrice;

              return (
                <motion.div
                  key={course.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={orderClass}
                >
                  <CourseCard
                    title={course.title}
                    tagline={course.tagline}
                    description={course.description}
                    image={course.staticImage}
                    video={course.videoUrl}
                    instructor={{
                      name: course.instructor,
                      image: course.instructorImage,
                    }}
                    gradient={course.gradient}
                    badge={course.badge}
                    ctaText={ctaText}
                    onCtaClick={() => {
                      if (isDisabled) return;
                      if (course.badge === "Invite Only") {
                        navigate(`/apply?program=${encodeURIComponent(course.title)}`);
                      } else {
                        handleCourseClick(course.title);
                      }
                    }}
                    isLoading={(checkoutLoading || paymentLoading) && course.badge !== "Invite Only"}
                  />
                </motion.div>
              );
            })}
            </div>

            {/* Section Title - Exclusive Programs */}
            {courses.length > 3 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="text-center mb-16"
              >
                <h3
                  className="text-3xl md:text-4xl font-bold mb-2"
                  style={{
                    fontFamily: "'Grift', sans-serif",
                    color: "#FFFFFF",
                    letterSpacing: "-0.02em",
                  }}
                >
                  <span style={{ color: "#E67E22" }}>Exclusive</span> Programs
                </h3>
              </motion.div>
            )}

            {/* Second Row - Bottom 3 Courses */}
            {courses.length > 3 && (
              <div className="relative grid md:grid-cols-2 lg:grid-cols-3 gap-8 px-4 md:px-0">
              {courses.slice(3).map((course, index) => {
                const isNiveles = course.title.toUpperCase().includes('NIVELES') || course.title.toUpperCase().includes('CHOREOGRAPHY');
                const coursePrice = isNiveles ? nivelesPrice : partnerworkPrice;
                const ctaText = course.badge === "Invite Only"
                  ? "Apply Now"
                  : isNiveles
                    ? (nivelesPrice ? `Purchase - ${displayPrice(nivelesPrice)}` : "Currently unavailable")
                    : (coursePrice ? `Purchase - ${displayPrice(coursePrice)}` : "Currently unavailable");

                const isDisabled = course.badge !== "Invite Only" && !coursePrice;

                return (
                  <motion.div
                    key={course.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                  >
                    <CourseCard
                      title={course.title}
                      tagline={course.tagline}
                      description={course.description}
                      image={course.staticImage}
                      video={course.videoUrl}
                      instructor={{
                        name: course.instructor,
                        image: course.instructorImage,
                      }}
                      gradient={course.gradient}
                      badge={course.badge}
                      ctaText={ctaText}
                      onCtaClick={() => {
                        if (isDisabled) return;
                        if (course.badge === "Invite Only") {
                          navigate(`/apply?program=${encodeURIComponent(course.title)}`);
                        } else {
                          handleCourseClick(course.title);
                        }
                      }}
                      isLoading={(checkoutLoading || paymentLoading) && course.badge !== "Invite Only"}
                    />
                  </motion.div>
                );
              })}
              </div>
            )}
          </>
          )}
        </div>
      </section>
    </main>
  );
};

export default Pricing;
